import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, Loader2, Sparkles, Wand2 } from "lucide-react";
import { StepFooter } from "./StepFooter";

interface DescribeStepProps {
  description: string;
  onDescriptionChange: (val: string) => void;
  isEnriched: boolean;
  onEnrichedChange: (val: boolean) => void;
  /** Called after "Fill Form with AI" succeeds — parent populates fields and advances step */
  onFillForm: () => Promise<void>;
  /** Called to enrich the description text */
  onEnrich: () => Promise<void>;
}

export function DescribeStep({
  description,
  onDescriptionChange,
  isEnriched,
  onEnrichedChange,
  onFillForm,
  onEnrich,
}: DescribeStepProps) {
  const { t } = useTranslation("search");
  const [isEnriching, setIsEnriching] = useState(false);
  const [isFillingForm, setIsFillingForm] = useState(false);

  const hasText = description.trim() !== "";

  // ── Phase logic ──────────────────────────────────────────────────────────
  // Phase A: empty textarea
  // Phase B: has text, not enriched
  // Phase C: enriched
  const phase: "A" | "B" | "C" = isEnriched ? "C" : hasText ? "B" : "A";

  async function handleEnrich(resetFirst = false) {
    if (resetFirst) onEnrichedChange(false);
    setIsEnriching(true);
    try {
      await onEnrich();
      onEnrichedChange(true);
    } finally {
      setIsEnriching(false);
    }
  }

  async function handleFillForm() {
    setIsFillingForm(true);
    try {
      await onFillForm();
    } finally {
      setIsFillingForm(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            {t("wizard.describeStep.cardTitle")}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3">
          <Textarea
            rows={4}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder={t("wizard.describeStep.placeholder")}
          />

          {/* ── Phase A: empty — disabled enrich button ── */}
          {phase === "A" && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                disabled
                className="text-[0.8125rem] px-3 py-1.5 h-auto opacity-45 cursor-not-allowed"
              >
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                {t("wizard.describeStep.enrichButton")}
              </Button>
            </div>
          )}

          {/* ── Phase B: has text, not enriched ── */}
          {phase === "B" && (
            <div className="flex justify-end">
              <Button
                variant="default"
                onClick={() => handleEnrich()}
                disabled={isEnriching}
                className="text-[0.8125rem] px-3 py-1.5 h-auto"
              >
                {isEnriching ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    {t("wizard.describeStep.enrichingButton")}
                  </>
                ) : (
                  <>
                    <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                    {t("wizard.describeStep.enrichButton")}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* ── Phase C: enriched ── */}
          {phase === "C" && (
            <div className="flex justify-between items-center">
              {/* Left: enriched badge */}
              <span className="text-success text-xs font-medium flex items-center gap-1">
                <Check className="h-3.5 w-3.5" />
                {t("wizard.describeStep.enrichedBadge")}
              </span>

              {/* Right: refine + fill form */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => handleEnrich(true)}
                  disabled={isEnriching || isFillingForm}
                  className="text-xs h-auto px-2.5 py-1.5"
                >
                  {isEnriching ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      {t("wizard.describeStep.enrichingButton")}
                    </>
                  ) : (
                    t("wizard.describeStep.refineButton")
                  )}
                </Button>

                <Button
                  variant="default"
                  onClick={handleFillForm}
                  disabled={isFillingForm || isEnriching}
                  className="text-[0.8125rem] px-3 py-1.5 h-auto"
                >
                  {isFillingForm ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      {t("wizard.describeStep.analyzingButton")}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      {t("wizard.describeStep.fillFormButton")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <StepFooter />
    </div>
  );
}
